import { noop, valueProvider, asis, arraysAreEqual, countArgs } from '@proc7ts/primitives';
import { Supply, neverSupply, isAlwaysSupply } from '@proc7ts/supply';
import { O as OnEvent__symbol, a as OnEvent$supplier, b as OnEvent$do, c as OnEvent$then, e as eventReceiver, A as AfterEvent$noFallback, d as AfterEvent__symbol, s as shareEvents, f as sendEventsTo, o as onceEvent, n as neverReceive, i as isEventKeeper, g as digEvents, E as EventNotifier, h as isEventSender, j as alwaysReceiveValue, k as neverReceiveBecause, t as translateEvents, l as supplyEvents } from './_fun-events.base.js';
export { d as AfterEvent__symbol, E as EventNotifier, N as NoEventsError, O as OnEvent__symbol, e as eventReceiver, i as isEventKeeper, h as isEventSender, f as sendEventsTo } from './_fun-events.base.js';

/**
 * Converts a plain event receiver registration function to {@link OnEvent} sender.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param register - Generic event receiver registration function. It will be called on each receiver registration,
 * unless the receiver's {@link EventReceiver.Generic.supply event supply} is cut off already.
 *
 * @returns An {@link OnEvent} sender registering event receivers with the given `register` function.
 */
function onEventBy(register) {
    const onEvent = ((receiver) => {
        const generic = eventReceiver(receiver);
        const { supply } = generic;
        if (!supply.isOff) {
            try {
                register(generic);
            }
            catch (error) {
                supply.off(error);
            }
        }
        return supply;
    });
    onEvent[OnEvent__symbol] = OnEvent$supplier;
    onEvent.do = OnEvent$do;
    onEvent.then = OnEvent$then;
    return onEvent;
}
/**
 * Checks whether the given value is an {@link OnEvent} sender.
 *
 * @category Core
 * @typeParam TEvent - Expected event type.
 * @typeParam TOther - Another type the value may have.
 * @param value - A value to check.
 *
 * @returns `true` if the `value` has been created by {@link onEventBy} function or in compatible way,
 * or `false` otherwise.
 */
function isOnEvent(value) {
    return typeof value === 'function'
        && value[OnEvent__symbol] === OnEvent$supplier
        && value.do === OnEvent$do
        && value.then === OnEvent$then;
}

/**
 * Converts a plain event receiver registration function to {@link AfterEvent} keeper with a fallback.
 *
 * The event constructed by `fallback` will be sent to the registered first receiver, unless `register` function sends
 * one.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param register - Generic event receiver registration function. It will be called on each receiver registration,
 * unless the receiver's {@link EventReceiver.Generic.supply event supply} is cut off already.
 * @param fallback - A function creating fallback event. When omitted, the initial event is expected to be sent by
 * `register` function. A receiver registration would lead to an error otherwise.
 * @param cleanup - A function that will be called once all registered event supplies cut off.
 *
 * @returns An {@link AfterEvent} keeper registering event receivers with the given `register` function.
 */
function afterEventBy(register, fallback = AfterEvent$noFallback, cleanup = AfterEvent$noCleanup) {
    let lastEvent;
    let numReceivers = 0;
    const afterEvent = ((receiver) => {
        let dest = noop;
        const generic = eventReceiver(receiver);
        if (generic.supply.isOff) {
            return generic.supply;
        }
        const supply = new Supply(noop).needs(generic);
        let reported = false;
        ++numReceivers;
        try {
            register({
                supply,
                receive: (context, ...event) => {
                    reported = true;
                    lastEvent = event;
                    dest(context, ...event);
                },
            });
        }
        catch (error) {
            supply.off(error);
        }
        if (!supply.isOff || reported) {
            if (!lastEvent) {
                try {
                    lastEvent = fallback();
                }
                catch (error) {
                    supply.off(error);
                }
            }
            if (lastEvent) {
                generic.receive({
                    onRecurrent(recurrent) {
                        dest = (_context, ...event) => recurrent(...event);
                    },
                }, ...lastEvent);
                dest = (context, ...event) => generic.receive(context, ...event);
            }
        }
        return supply.whenOff(reason => {
            if (!--numReceivers) {
                lastEvent = undefined;
            }
            generic.supply.off(reason);
            if (!numReceivers) {
                cleanup(reason);
            }
        });
    });
    afterEvent[OnEvent__symbol] = OnEvent$supplier;
    afterEvent.do = OnEvent$do;
    afterEvent.then = OnEvent$then;
    afterEvent[AfterEvent__symbol] = OnEvent$supplier;
    return afterEvent;
}
function AfterEvent$noCleanup(_reason) {
    // No-op `AfterEvent` cleanup
}
/**
 * Checks whether the given value is an {@link AfterEvent} keeper.
 *
 * @category Core
 * @typeParam TEvent - Expected event type.
 * @typeParam TOther - Another type the value may have.
 * @param value - A value to check.
 *
 * @returns `true` if the `value` has been created by {@link afterEventBy} function or in compatible way,
 * or `false` otherwise.
 */
function isAfterEvent(value) {
    return isOnEvent(value) && value[AfterEvent__symbol] === OnEvent$supplier;
}

/**
 * Builds an {@link AfterEvent} keeper of events sent by all event keepers in `sources` map.
 *
 * @category Core
 * @typeParam TSrcMap - A type of `sources` map.
 * @param sources - A map of named event keepers the events are originated from.
 *
 * @returns An event keeper sending a map of events received from each source keeper. Each event in this map has the
 * same key as its source keeper in `sources`.
 */
function afterAll(sources) {
    const keys = Object.keys(sources);
    const registerReceiver = (receiver) => {
        const { supply } = receiver;
        const dispatch = sendEventsTo(receiver);
        let send = noop;
        const result = {};
        keys.forEach((key) => {
            supply.needs(sources[key][AfterEvent__symbol]()((...event) => {
                result[key] = event;
                send();
            }).needs(supply));
        });
        if (!supply.isOff) {
            send = () => dispatch(result);
        }
    };
    const latestEvent = () => {
        const result = {};
        keys.forEach((key) => onceEvent(sources[key][AfterEvent__symbol]())({
            supply: new Supply(),
            receive: (_ctx, ...event) => result[key] = event,
        }));
        return [result];
    };
    return afterEventBy(shareEvents(afterEventBy(registerReceiver, latestEvent)));
}

/**
 * Builds an {@link AfterEvent} keeper of events sent by each of the `sources`.
 *
 * @category Core
 * @typeParam TEvent - A type of events sent by each source.
 * @param sources - An array of source event keepers.
 *
 * @returns An event keeper sending events received from each source keeper. Each event item is an event tuple
 * originated from source keeper under its index in `sources` array.
 */
function afterEach(...sources) {
    const registerReceiver = (receiver) => {
        const { supply } = receiver;
        const dispatch = sendEventsTo(receiver);
        let send = noop;
        const result = [];
        sources.forEach((source, index) => {
            supply.needs(source[AfterEvent__symbol]()((...event) => {
                result[index] = event;
                send();
            }).needs(supply));
        });
        if (!supply.isOff) {
            send = () => dispatch(...result);
        }
    };
    const latestEvent = () => {
        const result = [];
        sources.forEach(source => onceEvent(source[AfterEvent__symbol]())({
            supply: new Supply(),
            receive: (_ctx, ...event) => result.push(event),
        }));
        return result;
    };
    return afterEventBy(shareEvents(afterEventBy(registerReceiver, latestEvent)));
}

/**
 * An {@link AfterEvent} keeper that never sends any events.
 *
 * @category Core
 */
const afterNever = ( /*#__PURE__*/afterEventBy(neverReceive));

/**
 * Builds an {@link AfterEvent} keeper of events sent by the given `sender`.
 *
 * The event generated by `fallback` will be sent to the registered first receiver, unless `register` function sends
 * one.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param sender - An event sender.
 * @param fallback - A function creating fallback event. When omitted, the initial event is expected to be sent by
 * `register` function. A receiver registration would lead to an error otherwise.
 *
 * @returns An {@link AfterEvent} keeper of events either originated from the given `sender`, or `initial` one.
 */
function afterSent(sender, fallback) {
    return afterEventBy(receiver => sender[OnEvent__symbol]()(receiver), fallback);
}

function afterSupplied(supplier, fallback) {
    return isEventKeeper(supplier) ? supplier[AfterEvent__symbol]() : afterSent(supplier, fallback);
}

/**
 * Builds an {@link AfterEvent} keeper of the given `event`.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param event - An event that will be sent to all receivers upon registration.
 *
 * @returns An {@link AfterEvent} keeper that always sends the given `event`.
 */
function afterThe(...event) {
    return afterEventBy(noop, valueProvider(event));
}

function afterValue(value) {
    return isAfterEvent(value) ? value : afterThe(value);
}

/**
 * Creates an event processor that consumes incoming events.
 *
 * @category Event Processing
 * @typeParam TEvent - Incoming event type. This is a list of consumer function parameter types.
 * @param consume - A function consuming events. This function may return a {@link SupplyPeer peer of event supply},
 * e.g. when registers a nested event receiver. This supply will be cut off on new event, unless returned again.
 *
 * @returns A function accepting incoming event supplier and returning event supply that will stop consuming events once
 * cut off.
 */
function consumeEvents(consume) {
    return input => {
        let consumerSupply = neverSupply();
        // Do not use `.cuts()` here as `consumerSupply` is mutable
        const supply = new Supply(reason => consumerSupply.off(reason));
        input({
            supply,
            receive(_ctx, ...event) {
                const prevSupply = consumerSupply;
                try {
                    consumerSupply = (consume(...event) || neverSupply()).supply;
                }
                finally {
                    if (consumerSupply !== prevSupply) {
                        prevSupply.off();
                    }
                }
            },
        });
        return supply;
    };
}

/**
 * A processor of events incoming from {@link AfterEvent} keeper that shares outgoing events supply among all registered
 * receivers.
 *
 * The created keeper receives events from this one and sends to registered receivers. The shared keeper registers
 * a receiver in this one only once, when first receiver registered. And cuts off original events supply once all
 * event supplies do.
 *
 * @category Event Processing
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param supplier - Event keeper to share events from.
 *
 * @returns An {@link AfterEvent} keeper sharing a common supply of events.
 */
function shareAfter(supplier) {
    return afterEventBy(shareEvents(supplier));
}

let deduplicateAfter$default;
function deduplicateAfter(isSimilar, getCue) {
    return isSimilar || getCue
        ? deduplicateAfter$create(isSimilar, getCue)
        : (deduplicateAfter$default || (deduplicateAfter$default = deduplicateAfter$create()));
}
function deduplicateAfter$create(isSimilar, getCue) {
    const processor = deduplicateAfter_(isSimilar, getCue);
    return input => shareAfter(processor(input));
}
let deduplicateAfter_$default;
function deduplicateAfter_(isSimilar, getCue) {
    return isSimilar || getCue
        ? deduplicateAfter_$create(isSimilar, getCue)
        : (deduplicateAfter_$default || (deduplicateAfter_$default = deduplicateAfter_$create()));
}
const deduplicateAfter$noPrior = { /* magic value meaning there is no cue */};
function deduplicateAfter_$create(isSimilar = deduplicateAfter$isDuplicate, getCue = asis) {
    return input => {
        let prior = deduplicateAfter$noPrior;
        return afterEventBy(({ supply, receive }) => input({
            supply,
            receive(ctx, ...nextEvent) {
                const next = getCue(nextEvent);
                if (prior === deduplicateAfter$noPrior || !isSimilar(prior, next)) {
                    prior = next;
                    receive(ctx, ...nextEvent);
                }
            },
        }), undefined, _ => prior = deduplicateAfter$noPrior);
    };
}
function deduplicateAfter$isDuplicate(prior, next) {
    return arraysAreEqual(prior, next, Math.max(countArgs(prior), countArgs(next)));
}

function digAfter(extract, fallback) {
    const processor = digAfter_(extract, fallback);
    return input => shareAfter(processor(input));
}
function digAfter_(extract, fallback) {
    const extractKeeper = (...events) => {
        const extracted = extract(...events);
        return extracted && afterSupplied(extracted);
    };
    return input => afterEventBy(digEvents(input, extractKeeper), fallback);
}

/**
 * Event emitter is a handy implementation of {@link OnEvent} sender.
 *
 * Extends {@link EventNotifier} by making its {@link EventNotifier.on} method implement an {@link OnEvent} interface.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 */
class EventEmitter extends EventNotifier {
    constructor() {
        super(...arguments);
        /**
         * {@link OnEvent} sender of {@link send emitted} events.
         *
         * The `[OnEvent__symbol]` method is an alias of this one.
         */
        this.on = onEventBy(receiver => super.on(receiver));
    }
    [OnEvent__symbol]() {
        return this.on;
    }
}

/**
 * An {@link OnEvent} sender that never sends any events.
 *
 * @category Core
 */
const onNever = ( /*#__PURE__*/onEventBy(neverReceive));

/**
 * Builds an {@link OnEvent} sender of events supplied by the given `supplier`.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param supplier - An event supplier.
 *
 * @returns An {@link OnEvent} sender of events originated from the given `supplier`.
 */
function onSupplied(supplier) {
    return isEventSender(supplier) ? supplier[OnEvent__symbol]() : supplier[AfterEvent__symbol]();
}

/**
 * Builds an {@link OnEvent} sender of events sent by any of the given `suppliers`.
 *
 * The resulting event supply is cut off as soon as all source supplies do.
 *
 * @category Core
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param suppliers - Original event suppliers.
 *
 * @returns An {@link OnEvent} sender of all supplied events.
 */
function onAny(...suppliers) {
    if (!suppliers.length) {
        return onNever;
    }
    return onEventBy(shareEvents(onEventBy(({ supply, receive }) => {
        let remained = suppliers.length;
        const removeSupplier = (reason) => {
            if (!--remained) {
                supply.off(reason);
            }
        };
        suppliers.forEach(supplier => onSupplied(supplier)({
            supply: new Supply(removeSupplier).needs(supply),
            receive,
        }));
    })));
}

/**
 * Represents a promise as event sender.
 *
 * When the `promise` resolves successfully the resolved value is sent to registered event receivers. The events
 * supply is {@link Supply.off cut off} immediately after that without any reason specified.
 *
 * When the `promise` is rejected the events supply is {@link Supply.off cut off} with promise rejection reason.
 *
 * @category Core
 * @typeParam T - A type of value the promise is resolved to.
 * @param promise - A promise-like instance or raw value to represent as event sender.
 *
 * @returns An {@link OnEvent} sender of the given `promise` settlement event.
 */
function onPromise(promise) {
    const whenResolved = Promise.resolve(promise);
    let receive = (receiver) => {
        whenResolved.then(() => receive(receiver), () => receive(receiver));
    };
    whenResolved.then(value => {
        receive = alwaysReceiveValue(value);
    }).catch(e => {
        receive = neverReceiveBecause(e);
    });
    return onEventBy(receiver => receive(receiver));
}

/**
 * A processor of events incoming from {@link OnEvent} sender that shares outgoing events supply among all registered
 * receivers.
 *
 * The created sender receives events from this one and sends to registered receivers. The shared sender registers
 * a receiver in this one only once, when first receiver registered. And cuts off original events supply once all
 * supplies do.
 *
 * @category Event Processing
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param supplier - Event sender to share events from.
 *
 * @returns An {@link OnEvent} sender sharing a common supply of events.
 */
function shareOn(supplier) {
    return onEventBy(shareEvents(supplier));
}

/**
 * Creates an event processor that extracts event senders from incoming events.
 *
 * The outgoing events supply is cut off once the incoming events supply do.
 *
 * @category Event Processing
 * @typeParam TInEvent - Incoming events type.
 * @typeParam TOutEvent - Extracted events type.
 * @param extract - A function extracting outgoing event supplier from incoming event. May return `undefined` when
 * nothing extracted.
 *
 * @returns New event processor.
 */
function digOn(extract) {
    const processor = digOn_(extract);
    return input => shareOn(processor(input));
}
/**
 * Creates an event processor that extracts event senders from incoming events, and does not share the outgoing events
 * supply.
 *
 * The outgoing events supply is cut off once the incoming events supply do.
 *
 * @category Event Processing
 * @typeParam TInEvent - Incoming events type.
 * @typeParam TOutEvent - Extracted events type.
 * @param extract - A function extracting outgoing event supplier from incoming event. May return `undefined` when
 * nothing extracted.
 *
 * @returns New event processor.
 */
function digOn_(extract) {
    const extractSender = (...event) => {
        const extracted = extract(...event);
        return extracted && onSupplied(extracted);
    };
    return input => onEventBy(digEvents(input, extractSender));
}

/**
 * Creates an event processor that translates events incoming from {@link OnEvent} sender.
 *
 * The translated events expected to be sent by the given `translate` function.
 *
 * @category Event Processing
 * @typeParam TInEvent - Incoming event type.
 * @typeParam TOutEvent - Outgoing translated event type.
 * @param translate - Event translation function. Accepts an outgoing event sender function as first parameter,
 * and incoming event as the rest of them.
 *
 * @returns A mapping function of incoming event sender to another one.
 */
function translateOn(translate) {
    const mapper = translateOn_(translate);
    return input => shareOn(mapper(input));
}
/**
 * Creates an event processor that translates events incoming from {@link OnEvent} sender, and does not share the
 * outgoing events supply.
 *
 * The translated events expected to be sent by the given `translate` function.
 *
 * @category Event Processing
 * @typeParam TInEvent - Incoming event type.
 * @typeParam TOutEvent - Outgoing translated event type.
 * @param translate - Event translation function. Accepts an outgoing event sender function as first parameter,
 * and incoming event as the rest of them.
 *
 * @returns A mapping function of incoming event sender to another one.
 */
function translateOn_(translate) {
    return input => onEventBy(translateEvents(input, translate));
}

function filterOn(test) {
    const map = filterOn_(test);
    return supplier => shareOn(map(supplier));
}
function filterOn_(// eslint-disable-line @typescript-eslint/naming-convention
test) {
    return translateOn_((send, ...event) => test(...event) && send(...event));
}

function mapAfter(convert, fallback) {
    const mapper = mapAfter_(convert, fallback);
    return input => shareAfter(mapper(input));
}
function mapAfter_(// eslint-disable-line @typescript-eslint/naming-convention
convert, fallback) {
    return input => afterEventBy(translateEvents(input, (send, ...event) => send(convert(...event))), fallback && (() => [fallback()]));
}

/**
 * Creates an event processor that converts events incoming from {@link OnEvent} sender with the given converter
 * function.
 *
 * @category Event Processing
 * @typeParam TEvent - Incoming events type.
 * @typeParam TResult - Outgoing events type.
 * @param convert - A converter function that accepts incoming event as parameters and returns converted outgoing event.
 *
 * @returns New event mapper.
 */
function mapOn(convert) {
    const mapper = mapOn_(convert);
    return input => shareOn(mapper(input));
}
/**
 * Creates an event processor that converts events incoming from {@link OnEvent} sender with the given converter
 * function, and does not share the outgoing events supply.
 *
 * @category Event Processing
 * @typeParam TEvent - Incoming events type.
 * @typeParam TResult - Outgoing events type.
 * @param convert - A converter function that accepts incoming event as parameters and returns outgoing event value.
 *
 * @returns New event mapper.
 */
function mapOn_(// eslint-disable-line @typescript-eslint/naming-convention
convert) {
    return input => onEventBy(translateEvents(input, (send, ...event) => send(convert(...event))));
}

/**
 * A processor of the first event incoming from {@link AfterEvent} keeper.
 *
 * Cuts off the outgoing events supply after sending the first event.
 *
 * @category Event Processing
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param supplier - Event keeper to receive an event from.
 *
 * @returns New keeper of the first event.
 */
function onceAfter(supplier) {
    return afterEventBy(onceEvent(supplier));
}

/**
 * A processor of the first event incoming from {@link OnEvent} sender.
 *
 * Cuts off the outgoing event supply after sending the first event.
 *
 * @category Event Processing
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param supplier - Event sender to receive an event from.
 *
 * @returns New sender of the first event.
 */
function onceOn(supplier) {
    return onEventBy(onceEvent(supplier));
}

/**
 * A processor that asynchronously resolves incoming events and sends then in the order of their resolution.
 *
 * Receives events or their promises from the given event sender, and sends them once they are resolved. The original
 * order of events is not preserved. Instead each resolved event is sent along with its index in original order.
 *
 * The resulting events supply is cut off immediately once unresolved events supply is cut off, or some of incoming
 * event promises rejected. In the latter case the rejection reason is used as a reason to cut off.
 *
 * @category Event Processing
 * @typeParam T - A type of values the promises resolve to.
 * @param from - A sender of events or promise-like instances resolved to ones.
 *
 * @returns New `OnEvent` sender of resolved events and their indices in original order starting from `1`.
 */
function resolveOn(from) {
    return onEventBy(receiver => {
        const { supply } = receiver;
        const dispatch = sendEventsTo(receiver);
        let lastIndex = 0;
        from({
            supply,
            receive(_ctx, promise) {
                const index = ++lastIndex;
                Promise.resolve()
                    .then(() => promise)
                    .then(event => dispatch(event, index), reason => supply.off(reason));
            },
        });
    });
}

/**
 * Creates an event processor that passes events incoming from {@link OnEvent} sender until the `required` supply is
 * cut off.
 *
 * The outgoing events supply will be cut off once incoming event supply does, unless a second supply passed in.
 * In the latter case that supply will be cut off instead.
 *
 * @category Event Processing
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param required - A peer of required event supply. The returned processor does nothing when `alwaysSupply()`
 * specified.
 * @param dependentSupply - The supply to cut off on cutting off the incoming events supply.
 *
 * @returns New event mapper.
 */
function supplyOn(required, dependentSupply) {
    return isAlwaysSupply(required.supply)
        ? asis
        : (input) => onEventBy(supplyEvents(input, required, dependentSupply));
}

/**
 * A processor that asynchronously resolves incoming events and sends them in the order they are received.
 *
 * Receives events or their promises from the given event sender, and sends them once they are resolved in the same
 * order as they have been received. Mat send events in batches, e.g. when events resolved out of order.
 *
 * The resulting events supply is cut off if some of incoming event promises rejected. In this case the rejection reason
 * is used as a reason to cut off. If incoming events supply is cut off, then the resulting event supply will be cut off
 * too, but only after all incoming events resolved and sent.
 *
 * @category Event Processing
 * @typeParam TEvent - Resolved event type.
 * @param from - A sender of events or promise-like instances resolved to ones.
 *
 * @returns New `OnEvent` sender of resolved events.
 */
function resolveOnOrdered(from) {
    return onEventBy(receiver => {
        const { supply } = receiver;
        const dispatch = sendEventsTo(receiver);
        const sourceSupply = new Supply();
        let numInProcess = 0;
        const source = from.do(supplyOn(supply, sourceSupply), mapOn(event => {
            ++numInProcess;
            return event;
        }));
        let received = [];
        let numSent = 1;
        let numReceived = 0;
        sourceSupply.whenOff(reason => {
            if (!numInProcess) {
                supply.off(reason);
            }
        });
        resolveOn(source)({
            supply,
            receive(_ctx, event, index) {
                const i = index - numSent;
                received[i] = event;
                ++numReceived;
                if (numReceived > i) {
                    let toSend;
                    if (numReceived === received.length) {
                        // Can send all received events
                        toSend = received;
                        received = [];
                    }
                    else {
                        // Can send events up to `i`
                        toSend = received.splice(0, i + 1);
                    }
                    numSent += toSend.length;
                    numReceived -= toSend.length;
                    numInProcess -= toSend.length;
                    dispatch(...toSend);
                    if (!numInProcess && sourceSupply.isOff) {
                        supply.needs(sourceSupply);
                    }
                }
            },
        });
    });
}

/**
 * Creates an event processor that passes events incoming from {@link AfterEvent} keeper until the `required` supply
 * is cut off.
 *
 * The outgoing events supply will be cut off once incoming event supply does, unless a second supply passed in.
 * In the latter case that supply will be cut off instead.
 *
 * @category Event Processing
 * @typeParam TEvent - An event type. This is a list of event receiver parameter types.
 * @param required - A peer of required event supply. The returned processor does nothing when `alwaysSupply()`
 * specified.
 * @param dependentSupply - The supply to cut off on cutting off the incoming events supply.
 *
 * @returns New event mapper.
 */
function supplyAfter(required, dependentSupply) {
    return isAlwaysSupply(required.supply)
        ? asis
        : (input) => afterEventBy(supplyEvents(input, required, dependentSupply));
}

function translateAfter(translate, fallback) {
    const mapper = translateAfter_(translate, fallback);
    return input => shareAfter(mapper(input));
}
/**
 * Creates an event processor that translates events incoming from {@link OnEvent} sender with fallback, and does not
 * share the outgoing events supply.
 *
 * The translated events expected to be sent by the given `translate` function.
 *
 * The returned mapper does the same as the one created by {@link translateOn_} one, but returns an {@link AfterEvent}
 * keeper instead of {@link OnEvent} sender. This can not be always done without a `fallback`, as not every
 * transformation results to valid {@link EventKeeper}. E.g. when some events filtered out.
 *
 * @category Event Processing
 * @typeParam TInEvent - Incoming event type.
 * @typeParam TOutEvent - Outgoing translated event type.
 * @param translate - Event translation function. Accepts an outgoing event sender function as first parameter,
 * and incoming event as the rest of them.
 * @param fallback - A function creating fallback event. When omitted, the initial event is expected to be sent by
 * `translate` function. A receiver registration would lead to an error otherwise.
 *
 * @returns A mapping function of incoming event keeper to another one.
 */
function translateAfter_(translate, fallback) {
    return input => afterEventBy(translateEvents(input, translate), fallback);
}

function valueAfter(valueOf, fallback) {
    const mapper = valueAfter_(valueOf, fallback);
    return input => shareAfter(mapper(input));
}
function valueAfter_(// eslint-disable-line @typescript-eslint/naming-convention
valueOf, fallback) {
    return translateAfter_((send, ...event) => {
        const value = valueOf(...event);
        if (value != null && value !== false) {
            send(value);
        }
    }, (fallback && (() => [fallback()])));
}

/**
 * Creates an event processor that sends the values of incoming events.
 *
 * Events are valued by provided `value` function. The `null`, `undefined`, and `false` values are dropped.
 *
 * @category Event Processing
 * @typeParam TEvent - Incoming event type.
 * @typeParam TValue - Event value type.
 * @param valueOf - Event value detector function. Accepts incoming event as parameters and returns either its value,
 * or `false`/`null`/`undefined` to ignore it.
 *
 * @returns New event processor.
 */
function valueOn(valueOf) {
    const mapper = valueOn_(valueOf);
    return input => shareOn(mapper(input));
}
/**
 * Creates an event processor that sends the values of incoming events, and does not share the outgoing events supply.
 *
 * @category Event Processing
 * @typeParam TEvent - Incoming event type.
 * @typeParam TValue - Event value type.
 * @param valueOf - Event value detector function. Accepts incoming event as parameters and returns either its value,
 * or `false`/`null`/`undefined` to ignore it.
 *
 * @returns New event processor.
 */
function valueOn_(// eslint-disable-line @typescript-eslint/naming-convention
valueOf) {
    return translateOn_((send, ...event) => {
        const value = valueOf(...event);
        if (value != null && value !== false) {
            send(value);
        }
    });
}

function statePath(path) {
    return (Array.isArray(path) ? path : [path]);
}

/**
 * @internal
 */
class PathEntry {
    constructor(_drop) {
        this._drop = _drop;
        this.emitter = new EventEmitter();
        this._nested = new Map();
        this.emitter.on((path, newValue, oldValue) => {
            const key = path[0];
            const nested = this._nested.get(key);
            if (nested) {
                nested.emitter.send(path.slice(1), newValue, oldValue);
            }
        });
    }
    on(receiver) {
        const supply = this.emitter.on(receiver);
        return new Supply(reason => {
            supply.off(reason);
            this._dropIfEmpty();
        }).needs(supply);
    }
    nest(key, dontCreateMissing) {
        const found = this._nested.get(key);
        if (found || dontCreateMissing) {
            return found;
        }
        const created = new PathEntry(() => this._remove(key));
        this._nested.set(key, created);
        return created;
    }
    done(reason) {
        for (const nested of this._nested.values()) {
            nested.done(reason);
        }
        this.emitter.supply.off(reason);
    }
    _remove(key) {
        this._nested.delete(key);
        this._dropIfEmpty();
    }
    _dropIfEmpty() {
        if (!this._nested.size && this.emitter.size <= 1) {
            this._drop();
        }
    }
}
/**
 * @internal
 */
class Trackers {
    constructor() {
        this._root = new PathEntry(noop);
    }
    on(path, receiver) {
        return this._entry(path).on(receiver);
    }
    send(path, newValue, oldValue) {
        this._root.emitter.send(path, newValue, oldValue);
    }
    done(path, reason) {
        const entry = this._entry(path, true);
        if (entry) {
            entry.done(reason);
        }
    }
    _entry(path, dontCreateMissing) {
        let entry = this._root;
        for (const key of path) {
            const nested = entry.nest(key, dontCreateMissing);
            if (!nested) {
                return;
            }
            entry = nested;
        }
        return entry;
    }
}
/**
 * @internal
 */
class SubStateTracker {
    constructor(_trackers, _path) {
        this._trackers = _trackers;
        this._path = _path;
        this.onUpdate = onEventBy(receiver => this._trackers.on(this._path, receiver));
        this.update = (path, newValue, oldValue) => {
            this._trackers.send([...this._path, ...statePath(path)], newValue, oldValue);
        };
    }
    get _tracker() {
        return this;
    }
    [OnEvent__symbol]() {
        return this.onUpdate;
    }
    track(path) {
        path = statePath(path);
        if (!path.length) {
            return this; // Path to itself.
        }
        return new SubStateTracker(this._trackers, [...this._path, ...path]);
    }
    done(reason) {
        this._trackers.done(this._path, reason);
    }
}
/**
 * State changes tracker.
 *
 * A state is a tree-like structure of sub-states (nodes) available under {@link StatePath}.
 *
 * When node modified a {@link StateTracker.update} should be called. Then all state update receivers registered by
 * {@link StateTracker.onUpdate} will receive this update.
 *
 * @category State Tracking
 */
class StateTracker {
    constructor() {
        /**
         * @internal
         */
        this._tracker = new SubStateTracker(new Trackers(), []);
    }
    /**
     * {@link OnStateUpdate state updates sender}.
     *
     * A state update will be sent to it whenever an `update()` function is called.
     *
     * The `[OnEvent__symbol]` property is an alias of this one.
     *
     * @returns State updates sender.
     */
    get onUpdate() {
        return this._tracker.onUpdate;
    }
    [OnEvent__symbol]() {
        return this.onUpdate;
    }
    // noinspection JSCommentMatchesSignature
    /**
     * Updates the component state.
     *
     * All receivers registered with {@link onUpdate} will receive this update.
     *
     * @typeParam T - A type of changed value.
     * @param key - Changed value key.
     * @param newValue - New value.
     * @param oldValue - Previous value.
     */
    get update() {
        return this._tracker.update;
    }
    /**
     * Starts tracking of partial state under the given path.
     *
     * @param path - A path to state part.
     *
     * @return New partial state tracker.
     */
    track(path) {
        const subTracker = this._tracker.track(path);
        return subTracker === this._tracker ? this : subTracker;
    }
    /**
     * Unregisters updates receivers and cuts off their supplies.
     *
     * After this method call the updates receivers of this partial state and all nested states won't receive any updates.
     *
     * @param reason - An optional reason to stop tracking.
     */
    done(reason) {
        this._tracker.done(reason);
    }
}

/**
 * Value accessor and changes tracker.
 *
 * Implements an {@link EventSender} interface by sending value changes to registered receivers as a pair of new and old
 * values.
 *
 * Implements an {@link EventKeeper} interface by sending current value and its updates.
 *
 * @category Value Tracking
 * @typeParam T - Tracked value type.
 */
class ValueTracker {
    constructor() {
        /**
         * @internal
         */
        this._by = neverSupply();
        /**
         * {@link AfterEvent} keeper of current value.
         *
         * The `[AfterEvent__symbol]` property is an alias of this one.
         *
         * @returns Current value keeper.
         */
        this.read = afterEventBy(receiver => this.on(receiveNewValue(receiver)), () => [this.it]);
    }
    [OnEvent__symbol]() {
        return this.on;
    }
    [AfterEvent__symbol]() {
        return this.read;
    }
    by(supplier, extract) {
        const acceptValuesFrom = (sender) => {
            const onValue = isEventKeeper(sender) ? sender[AfterEvent__symbol]() : sender[OnEvent__symbol]();
            return onValue(value => this.it = value);
        };
        this.byNone();
        if (!extract) {
            const sender = supplier;
            this._by = acceptValuesFrom(sender);
        }
        else {
            const container = supplier;
            this._by = onSupplied(container).do(consumeEvents((...event) => {
                const sender = extract(...event);
                if (sender) {
                    return acceptValuesFrom(sender);
                }
                return;
            }));
        }
        this._by.whenOff(() => this._by = neverSupply());
        return this;
    }
    /**
     * Unbinds the tracked value from any value supplier this tracker is {@link ValueTracker.by updated by}.
     *
     * If the tracker is not bound then does nothing.
     *
     * @param reason - Arbitrary reason of unbinding the value.
     *
     * @returns `this` instance.
     */
    byNone(reason) {
        this._by.off(reason);
        return this;
    }
}
/**
 * @internal
 */
function receiveNewValue(valueReceiver) {
    return {
        supply: valueReceiver.supply,
        receive(context, newValue) {
            valueReceiver.receive({
                onRecurrent(recurrentReceiver) {
                    context.onRecurrent(recurrentValue => recurrentReceiver(recurrentValue));
                },
            }, newValue);
        },
    };
}

/**
 * @internal
 */
class TrackedValue extends ValueTracker {
    constructor(_it) {
        super();
        this._it = _it;
        this._on = new EventEmitter();
    }
    get supply() {
        return this._on.supply;
    }
    get on() {
        return this._on.on;
    }
    get it() {
        return this._it;
    }
    set it(value) {
        const oldValue = this._it;
        if (oldValue !== value) {
            this._it = value;
            this._on.send(value, oldValue);
        }
    }
}
function trackValue(initial) {
    return new TrackedValue(initial);
}
function trackValueBy(supplier, extract) {
    return trackValue().by(supplier, extract);
}

/**
 * Synchronizes tracked values with each other.
 *
 * Any change to any of the added tracked values would update all the others.
 *
 * @category Value Tracking
 */
class ValueSync extends ValueTracker {
    constructor(initial) {
        super();
        /**
         * @internal
         */
        this._on = new EventEmitter();
        this._it = initial;
    }
    get on() {
        return this._on.on;
    }
    get supply() {
        return this._on.supply;
    }
    get it() {
        return this._it;
    }
    set it(value) {
        const old = this.it;
        if (old !== value) {
            this._it = value;
            this._on.send(value, old);
        }
    }
    sync(first, second, third) {
        let syncWithTracker = (tracker) => syncTrackers(this, tracker);
        let source;
        let extract;
        if (typeof first === 'string') {
            if (first === 'in') {
                syncWithTracker = tracker => syncTrackers(tracker, this);
            }
            source = second;
            extract = third;
        }
        else {
            source = first;
            extract = second;
        }
        const extractTracker = extract;
        if (!extractTracker) {
            return syncWithTracker(source);
        }
        const supplier = source;
        return (isEventKeeper(supplier) ? afterSupplied(supplier) : onSupplied(supplier)).do(consumeEvents((...event) => {
            const tracker = extractTracker(...event);
            return tracker && syncWithTracker(tracker);
        }));
        function syncTrackers(tracker1, tracker2) {
            const supply1 = tracker1.read(value => {
                tracker2.it = value;
            });
            const supply2 = tracker2.on(value => {
                tracker1.it = value;
            });
            return new Supply(reason => {
                supply2.off(reason);
                supply1.off(reason);
            }).needs(supply1).needs(supply2);
        }
    }
}

export { EventEmitter, StateTracker, ValueSync, ValueTracker, afterAll, afterEach, afterEventBy, afterNever, afterSent, afterSupplied, afterThe, afterValue, consumeEvents, deduplicateAfter, deduplicateAfter_, digAfter, digAfter_, digOn, digOn_, filterOn, filterOn_, isAfterEvent, isOnEvent, mapAfter, mapAfter_, mapOn, mapOn_, onAny, onEventBy, onNever, onPromise, onSupplied, onceAfter, onceOn, resolveOn, resolveOnOrdered, shareAfter, shareOn, statePath, supplyAfter, supplyOn, trackValue, trackValueBy, translateAfter, translateAfter_, translateOn, translateOn_, valueAfter, valueAfter_, valueOn, valueOn_ };
//# sourceMappingURL=fun-events.js.map
